import { useState, useEffect, useRef } from 'react';
import { medicationsAPI, doseLogsAPI } from '../api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Check, X, ChevronDown, ChevronUp, PartyPopper } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const TodayScreen = () => {
  const [todayData, setTodayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [selectedMed, setSelectedMed] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [expandedSlots, setExpandedSlots] = useState({});
  
  const currentSlotRef = useRef(null);

  const slotNames = {
    before_breakfast: 'Before Breakfast',
    breakfast: 'After Breakfast',
    before_lunch: 'Before Lunch',
    lunch: 'After Lunch',
    before_dinner: 'Before Dinner',
    dinner: 'After Dinner',
    bed: 'Before Sleep',
    prn: 'As Needed',
  };

  const skipReasons = [
    { value: 'forgot', label: 'Forgot' },
    { value: 'felt_unwell', label: 'Felt Unwell' },
    { value: 'not_at_home', label: 'Not at Home' },
    { value: 'other', label: 'Other' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Auto-scroll to current slot after data loads
    if (todayData && currentSlotRef.current) {
      setTimeout(() => {
        currentSlotRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayData?.current_slot]);

  const loadData = async () => {
    try {
      const data = await medicationsAPI.getToday();
      setTodayData(data);
      
      // Expand current slot by default
      if (data.current_slot) {
        setExpandedSlots(prev => ({ ...prev, [data.current_slot]: true }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const scrollToCurrentSlot = () => {
    if (currentSlotRef.current) {
      currentSlotRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Optimistic update helper
  const updateMedStatus = (medId, slot, status) => {
    setTodayData(prev => {
      if (!prev) return prev;
      
      const newSlots = prev.slots.map(slotData => {
        if (slotData.slot !== slot) return slotData;
        
        return {
          ...slotData,
          medications: slotData.medications.map(med => {
            if (med.medication.id !== medId) return med;
            return {
              ...med,
              status: status,
              taken: status === 'taken',
              skipped: status === 'skipped',
            };
          })
        };
      });
      
      // Recalculate totals
      const completed = newSlots.reduce((acc, s) => 
        acc + s.medications.filter(m => m.taken || m.skipped).length, 0);
      const total = newSlots.reduce((acc, s) => acc + s.medications.length, 0);
      
      return {
        ...prev,
        slots: newSlots,
        completed_doses: completed,
        all_done: completed >= total && total > 0
      };
    });
  };

  const handleTaken = async (med, slot) => {
    // Optimistic update - instant UI feedback
    updateMedStatus(med.medication.id, slot, 'taken');
    toast.success('Marked as taken!');
    
    // API call in background
    try {
      const today = new Date().toISOString().split('T')[0];
      await doseLogsAPI.create({
        medication_id: med.medication.id,
        scheduled_date: today,
        routine_slot: slot,
        status: 'taken',
      });
    } catch (error) {
      // Revert on failure
      updateMedStatus(med.medication.id, slot, null);
      toast.error('Failed to save. Please try again.');
    }
  };

  const handleSkip = (med, slot) => {
    setSelectedMed(med);
    setSelectedSlot(slot);
    setSkipDialogOpen(true);
  };

  const confirmSkip = async (reason) => {
    if (!selectedMed || !selectedSlot) return;
    
    const medId = selectedMed.medication.id;
    const slot = selectedSlot;

    // Optimistic update - instant UI feedback
    updateMedStatus(medId, slot, 'skipped');
    toast.success('Marked as skipped');
    setSkipDialogOpen(false);
    setSelectedMed(null);
    setSelectedSlot(null);
    
    // API call in background
    try {
      const today = new Date().toISOString().split('T')[0];
      await doseLogsAPI.create({
        medication_id: medId,
        scheduled_date: today,
        routine_slot: slot,
        status: 'skipped',
        skip_reason: reason,
      });
    } catch (error) {
      // Revert on failure
      updateMedStatus(medId, slot, null);
      toast.error('Failed to save. Please try again.');
    }
  };

  const toggleSlotExpanded = (slot) => {
    setExpandedSlots(prev => ({ ...prev, [slot]: !prev[slot] }));
  };

  const getSlotStatus = (slotData) => {
    const total = slotData.medications.length;
    const done = slotData.medications.filter(m => m.taken || m.skipped).length;
    return { total, done, allDone: done >= total };
  };

  const isSlotPast = (slot) => {
    if (!todayData?.current_slot) return false;
    const slotOrder = ['before_breakfast', 'breakfast', 'before_lunch', 'lunch', 'before_dinner', 'dinner', 'bed', 'prn'];
    return slotOrder.indexOf(slot) < slotOrder.indexOf(todayData.current_slot);
  };

  const isSlotFuture = (slot) => {
    if (!todayData?.current_slot) return false;
    const slotOrder = ['before_breakfast', 'breakfast', 'before_lunch', 'lunch', 'before_dinner', 'dinner', 'bed', 'prn'];
    return slotOrder.indexOf(slot) > slotOrder.indexOf(todayData.current_slot);
  };

  const formatDosage = (med) => {
    // Extract short dosage from instructions or medication name
    const name = med.medication.display_name || '';
    const instructions = med.medication.instructions_text || '';
    
    // Try to extract dosage pattern like "Take 2 tab" or "1 capsule"
    const dosageMatch = instructions.match(/take\s+(\d+)\s*(tab|cap|ml|tablet|capsule)/i);
    if (dosageMatch) {
      return `Take ${dosageMatch[1]} ${dosageMatch[2]}`;
    }
    
    // Fallback to frequency
    const freq = med.medication.frequency_per_day;
    if (freq) {
      return `${freq}x daily`;
    }
    
    return '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="today-screen">
        <div className="text-2xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!todayData?.slots?.length) {
    return (
      <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen p-6" data-testid="today-screen">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Today</h1>
        <div className="text-center py-16">
          <p className="text-xl text-gray-600">No medications scheduled for today</p>
          <p className="text-base text-gray-500 mt-2">Add medications to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen pb-24" data-testid="today-screen">
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-gray-900">Today</h1>
          {todayData.current_slot && (
            <span className="text-base text-gray-500">
              Now: {slotNames[todayData.current_slot]}
            </span>
          )}
        </div>

        {/* All Done Celebration */}
        {todayData.all_done && (
          <div className="bg-green-100 border-2 border-green-400 rounded-2xl p-6 text-center" data-testid="all-done-banner">
            <PartyPopper className="h-12 w-12 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-800">All Done!</p>
            <p className="text-lg text-green-700">You have taken all medications for today</p>
          </div>
        )}

        {/* Slots */}
        {todayData.slots.map((slotData) => {
          const status = getSlotStatus(slotData);
          const isCurrent = slotData.slot === todayData.current_slot;
          const isPast = isSlotPast(slotData.slot);
          const isFuture = isSlotFuture(slotData.slot);
          const isExpanded = expandedSlots[slotData.slot] ?? isCurrent;

          return (
            <div
              key={slotData.slot}
              ref={isCurrent ? currentSlotRef : null}
              className={`rounded-2xl border-2 overflow-hidden transition-all ${
                isCurrent 
                  ? 'border-green-500 bg-white shadow-lg' 
                  : isPast 
                    ? 'border-gray-200 bg-gray-50' 
                    : 'border-gray-200 bg-white opacity-70'
              }`}
              data-testid={`slot-${slotData.slot}`}
            >
              {/* Slot Header */}
              <button
                onClick={() => toggleSlotExpanded(slotData.slot)}
                className={`w-full p-4 flex items-center justify-between ${
                  isCurrent ? 'bg-green-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xl font-bold ${isCurrent ? 'text-green-700' : 'text-gray-700'}`}>
                    {slotNames[slotData.slot]}
                  </span>
                  {status.allDone && (
                    <span className="bg-green-500 text-white text-sm font-semibold px-2 py-1 rounded-full">
                      ✓ Done
                    </span>
                  )}
                  {!status.allDone && (
                    <span className="bg-gray-200 text-gray-700 text-sm font-semibold px-2 py-1 rounded-full">
                      {status.done}/{status.total}
                    </span>
                  )}
                </div>
                {isExpanded ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
              </button>

              {/* Medications List */}
              {isExpanded && (
                <div className="p-3 space-y-3">
                  {slotData.medications.map((med, idx) => (
                    <div
                      key={`${med.medication.id}-${idx}`}
                      className={`rounded-xl p-4 ${
                        med.taken 
                          ? 'bg-green-50 border border-green-200' 
                          : med.skipped 
                            ? 'bg-gray-100 border border-gray-200' 
                            : 'bg-white border border-gray-200'
                      }`}
                      data-testid={`med-${med.medication.id}-${slotData.slot}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className={`text-xl font-bold truncate ${med.taken ? 'text-green-800' : med.skipped ? 'text-gray-500' : 'text-gray-900'}`}>
                            {med.medication.display_name}
                          </p>
                          <p className="text-base text-gray-600">
                            {formatDosage(med)}
                          </p>
                        </div>

                        {/* Status or Buttons */}
                        {med.taken ? (
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-green-700">
                              <Check className="h-6 w-6" />
                              <span className="text-lg font-semibold">Taken</span>
                            </div>
                          </div>
                        ) : med.skipped ? (
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-gray-500">
                              <X className="h-6 w-6" />
                              <span className="text-lg font-semibold">Skipped</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              data-testid={`btn-taken-${med.medication.id}`}
                              onClick={() => handleTaken(med, slotData.slot)}
                              className="h-14 px-5 text-lg font-bold bg-green-600 hover:bg-green-700"
                            >
                              Taken
                            </Button>
                            <Button
                              data-testid={`btn-skip-${med.medication.id}`}
                              onClick={() => handleSkip(med, slotData.slot)}
                              variant="outline"
                              className="h-14 px-4 text-lg font-semibold"
                            >
                              Skip
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Skip Dialog */}
      <Dialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Why are you skipping?</DialogTitle>
            <DialogDescription className="text-base">
              This helps track your medication patterns
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 mt-4">
            {skipReasons.map((reason) => (
              <Button
                key={reason.value}
                onClick={() => confirmSkip(reason.value)}
                variant="outline"
                className="h-14 text-lg font-semibold justify-start"
              >
                {reason.label}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TodayScreen;
